import { useRef, useState } from "react";
import { bool, object, func } from "prop-types";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { useFeatureGuardian } from "@hooks";
import { LOCKED_FEATURES } from "@utils";
import {
  LabelCreate,
  LabelsDialog,
  LabelSelectItem,
  useEstimateEditorSettings,
  useEstimateEditorStore,
  useEstimateStructure, useEstimateTableSettings
} from "@tools";
import { MenuButton, PopMenu } from "@components";
import { Divider, Grid } from "@material-ui/core";
import { Settings } from "@assets";
import { Add } from "@material-ui/icons";
import useStyle from "./LabelSelect.style";
import classnames from "classnames";

export const LabelSelect = observer(({
  element,
  show,
  onClose,
  onAddedLabels,
  anchorEl,
}) => {
  
  const { t } = useTranslation();
  const { isTemplate } = useEstimateEditorSettings();
  const { companyLabels, customLabels } = useEstimateEditorStore();
  const { showLabelsColumn, setShowLabelsColumn } = useEstimateTableSettings();
  const structure = useEstimateStructure();
  const classes = useStyle();
  
  const [showLabelManager, setShowLabelManager] = useState(false);
  const [showLabelCreate, setShowLabelCreate] = useState(false);
  
  const labelCreateAnchor = useRef(null);
  
  const { checkAccess, FeatureAlert } = useFeatureGuardian(
    LOCKED_FEATURES.LABELS
  );
  
  const handleClose = (e) => {
    onClose(e, false);
  }
  
  const handleLabelAdd = async (label) => {
    const allowed = await checkAccess();
    if (!allowed) return;
    
    if(!structure.addedLabels?.find(l => l.systemId === label.id)) {
      structure.addLabel(label);
    }
    if(!showLabelsColumn)
      setShowLabelsColumn();
    element.addLabel(label.id);
    onAddedLabels?.(true);
  }
  
  const handleLabelUpdate = (label) => {
    structure.updateLabel(label.id, label);
  }
  
  const handleLabelRemove = (label) => {
    element.removeLabel(label.id);
    if(!structure.usedLabels.includes(label.id))
      structure.removeLabel(label.id);
  }
  
  return (
    <PopMenu
      show={show}
      anchor={anchorEl}
      id="labelSelector"
      onClickAway={handleClose}
      style={{ maxWidth: 320 }}
      className="py-2"
      placement="right"
      // clickAwayMouseEvent="onClick"
    >
      <Grid
        item container
        className="max-h-list w-full overflow-auto"
      >
        {
          (!companyLabels?.length && !customLabels?.length) &&
          <p className="text-secondary my-2 mx-4">
            { t("views.editor.labels.not_found") }
          </p>
        }
        {
          customLabels.length > 0 &&
          <span className={classes.recommended}>
            {t(`views.editor.labels.${isTemplate ? "template" : "project"}`)}
          </span>
        }
        {
          customLabels.map((label) => (
            <LabelSelectItem
              key={`${label.name}_${label.id}`}
              label={label}
              isUsed={Boolean(element.labels?.includes(label.id))}
              onAdd={handleLabelAdd}
              onRemove={handleLabelRemove}
            >
              { label.name }
            </LabelSelectItem>
          ))
        }
        {
          companyLabels.length > 0 &&
          <span className={classnames(classes.recommended, "mt-2")}>
            {t("views.editor.labels.company")}
          </span>
        }
        {companyLabels.map((label) => (
          <LabelSelectItem
            key={`${label.name}_${label.id}`}
            label={label}
            isUsed={Boolean(element.labels?.includes(label.id))}
            onAdd={handleLabelAdd}
            onRemove={handleLabelRemove}
          >
            { label.name }
          </LabelSelectItem>
        ))}
        <Grid item container className={classes.manager}>
          <Divider className="w-full" />
          <MenuButton
            ref={labelCreateAnchor}
            icon={<Add />}
            color="primary"
            onClick={() => setShowLabelCreate(!showLabelCreate)}
          >
            {t("views.settings.projects.labels.add_label")}
          </MenuButton>
          <MenuButton
            icon={<Settings />}
            color="primary"
            onClick={() => setShowLabelManager(!showLabelManager)}
          >
            {t("views.settings.menu_full.labels")}
          </MenuButton>
        </Grid>
        <LabelsDialog
          open={showLabelManager}
          onClose={() => setShowLabelManager(false)}
          onCreateLabel={handleLabelAdd}
          onUpdateLabel={handleLabelUpdate}
        />
        <LabelCreate
          anchorEl={labelCreateAnchor.current}
          show={showLabelCreate}
          onClose={() => setShowLabelCreate(false)}
          onCreateLabel={handleLabelAdd}
        />
        <FeatureAlert />
      </Grid>
    </PopMenu>
  )
})

LabelSelect.propTypes = {
  element: object.isRequired,
  anchorEl: object.isRequired,
  show: bool.isRequired,
  onClose: func.isRequired,
  onAddedLabels: func,
}